import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { accountTreeModel } from 'entities/account';
import { TIME_ZONES } from 'shared/config';
import { $t, translated } from 'shared/lib';
import { accountRoute } from 'shared/routing';
import { createDateFilter, createRegistry, MultiSelect, RegistryRootProps } from 'shared/ui';
import { AccountTransactionRegistryModel } from '../../model';
import cls from './account-transaction-registry.module.scss';

export type AccountTransactionRegistryProps = RegistryRootProps & {
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
	withTitle?: boolean;
};

export const createAccountTransactionRegistry = ({
	registryModel,
	dateFilterModel,
}: AccountTransactionRegistryModel) => {
	const Registry = createRegistry(registryModel);

	const DateFilter = createDateFilter(dateFilterModel);

	const DateField = reflect({
		view: DateFilter.DateField,
		bind: {
			'data-test-id': 'account-transaction-registry-date-input',
			className: cls.accountTransactionRegistryDateFilter,
		},
	});

	const TimeZoneField = reflect({
		view: DateFilter.TimeZoneField,
		bind: {
			'data-test-id': 'account-transaction-registry-time-zone-filter',
			className: cls.accountTransactionRegistryTimeZoneFilter,
			value: registryModel.filters.timeZone.$value.map((tz) => tz.toString()),
			onChange: (value) =>
				registryModel.filters.timeZone.setValue(Number(value ?? TIME_ZONES['UTC-00:00'])),
		},
	});

	const CurrenciesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'account-transaction-registry-currencies-filter',
			label: translated('account-transaction-registry.filters.currencies.label'),
			placeholder: translated('account-transaction-registry.filters.currencies.placeholder'),
			className: cls.accountTransactionRegistryCurrenciesFilter,
			value: registryModel.filters.currencies.$value,
			onChange: (value) => registryModel.filters.currencies.setValue(value),
			data: accountTreeModel.$currencies,
			searchable: true,
		},
	});

	const TypesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'account-transaction-registry-types-filter',
			label: translated('account-transaction-registry.filters.types.label'),
			placeholder: translated('account-transaction-registry.filters.types.placeholder'),
			nothingFoundMessage: translated('account-transaction-registry.filters.types.empty'),
			className: cls.accountTransactionRegistryTypesFilter,
			value: registryModel.filters.types.$value,
			onChange: (value) => registryModel.filters.types.setValue(value),
			data: $t.map((t) =>
				['deposit', 'withdraw'].map((type) => ({
					value: type,
					label: t(`account-transaction-registry.types.${type}`),
				})),
			),
			searchable: true,
		},
	});

	const AccountLink = reflect({
		view: Anchor<typeof Link>,
		bind: {
			to: accountRoute,
			component: Link,
		},
	});

	const AccountTransactionRegistry = forwardRef<HTMLDivElement, AccountTransactionRegistryProps>(
		({ emptyActionSlot, topBarActionSlot, withTitle = true, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('account-transaction-registry.title')}</Registry.Title>}
					<Registry.TopBar
						alwaysShow
						className={cls.accountTransactionRegistryTopBar}
					>
						<Registry.Filters className={cls.accountTransactionRegistryFilters}>
							<DateField />
							<TimeZoneField />
							<CurrenciesField />
							<TypesField />
						</Registry.Filters>
						{topBarActionSlot}
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col className={cls.accountTransactionRegistryColDate} />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='date'>
									{t('account-transaction-registry.table-columns.date')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='from'>
									{t('account-transaction-registry.table-columns.from')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='to'>
									{t('account-transaction-registry.table-columns.to')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='currency'>
									{t('account-transaction-registry.table-columns.currency')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='type'>
									{t('account-transaction-registry.table-columns.type')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='amount'>
									{t('account-transaction-registry.table-columns.amount')}
								</Registry.Table.ThSorter>
								<Registry.Table.Th>
									{t('account-transaction-registry.table-columns.balance-before')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-transaction-registry.table-columns.balance-after')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										<Registry.Table.Td>{row.date}</Registry.Table.Td>
										<Registry.Table.Td>
											{row.from && (
												<AccountLink
													data-test-id={`account-transaction-registry-from-link-${row.from.id}`}
													query={{ id: row.from.id }}
												>
													{row.from.name}
												</AccountLink>
											)}
										</Registry.Table.Td>
										<Registry.Table.Td>
											{row.to && (
												<AccountLink
													data-test-id={`account-transaction-registry-to-link-${row.to.id}`}
													query={{ id: row.to.id }}
												>
													{row.to.name}
												</AccountLink>
											)}
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td className={cls.accountTransactionRegistryType}>
											{t(`account-transaction-registry.types.${row.type}`, {
												defaultValue: row.type,
											})}
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.amount} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.balanceBefore} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.balanceAfter} />
										</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('account-transaction-registry.empty.title')}
						message={t('account-transaction-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('account-transaction-registry.table-empty.title')}
						message={t('account-transaction-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return AccountTransactionRegistry;
};
